'use memo'

import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation'
import { MenuTitles, Pages } from 'constants/menu'
import { AgencyContext } from 'contexts'
import { ContentLayout, PageLayout } from 'layout'
import { AgxToast, enumToOptions } from '@urbanx/agx-ui-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { StaffAction } from 'types/staff'
import { AgxToastState, NotificationState } from 'types/commonTypes'
import { UploadPublicDocument } from 'services'
import { isEqual } from 'lodash-es'

import { FormPrompt } from '../../components/FormPrompt'
import RoleAssignmentModal from './RoleAssignmentModel'
import StaffMemberFormPage, {
  StaffMemberFormValues,
} from './StaffMemberFormPage'
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles'
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client'
import {
  StaffModel,
  UploadedDocument,
  useDeleteStaffMember,
  useGetAllStaff,
  useInvalidateUseGetAllStaff,
  useInvalidateUseGetStaff,
  useSendInvitationEmail,
  useUpdateStaffMember,
} from '@urbanx/agx.staff.client'
import { getEnumValueByValue } from 'helpers/enumHelper'
import { isDirtyEqual } from 'utils/compares'
import {
  AgenciesApiContext,
  UrbanXTeamByAgenciesResponse,
  useAssignStaffMemberToAgencies,
  useGetAgenciesAssignedToStaffMember,
  useGetAllAgencies,
  useInvalidateUseGetUrbanXTeamForAgency,
  useInvalidateUseGetStaffMembersAssignedToAgencies,
  useRemovePublicDocument,
} from '@urbanx/agx.agencies.client'
import {
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxSaveChangesButton,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import {
  dedupeRoleConflicts,
  getAvailableStaffRoleOptions,
  mapAgenciesToOptions,
  rolesAssignableToAgency,
  rolesWithStateAccess,
  staffStateOptions,
  validateStaffMemberForm,
} from './staffPageUtils'
import { isEmailValid } from 'helpers/emailHelper'

const staffRoleOptions = enumToOptions(StaffRole)
const emptyStaffMember: StaffModel = {}

const EditStaffMember = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userId } = useParams<{ userId?: string }>()
  const staffRouteKey = userId?.trim()
  const [selectedAgency] = useContext(AgencyContext)
  const { userAccount, getAuthToken } = useContext(AuthContext)
  const loggedInStaffId = userAccount?.agent || ''
  const onBack = () => navigate(Pages.STAFF)

  const { data: allStaffMembers, isPending: allStaffMembersPending } =
    useGetAllStaff()
  const invalidateGetAllStaff = useInvalidateUseGetAllStaff()
  const invalidateGetStaff = useInvalidateUseGetStaff()
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency()
  const queryClient = useQueryClient()
  const createdStaffFromRouteState = location.state?.createdStaff as
    | StaffMemberFormValues
    | undefined

  const staffMember = useMemo(
    () =>
      staffRouteKey
        ? allStaffMembers?.find((staff) => staff.id === staffRouteKey)
        : undefined,
    [allStaffMembers, staffRouteKey]
  )

  const routeStateMatchesCurrentUserId =
    !!staffRouteKey &&
    !!createdStaffFromRouteState &&
    createdStaffFromRouteState.id === staffRouteKey

  const selectedStaffMember =
    staffMember ??
    (routeStateMatchesCurrentUserId ? createdStaffFromRouteState : undefined) ??
    emptyStaffMember

  const isMissingStaffMember =
    !userId || (!!userId && !allStaffMembersPending && !selectedStaffMember.id)

  useEffect(() => {
    if (isMissingStaffMember) {
      navigate(Pages.STAFF, { replace: true })
    }
  }, [isMissingStaffMember, navigate])

  const [staffMemberForm, setStaffMemberForm] = useState<StaffMemberFormValues>(
    {
      ...selectedStaffMember,
      roles: selectedStaffMember.roles,
    }
  )
  const [img, setImg] = useState<string | undefined>(
    selectedStaffMember.photo?.publicPath
  )
  const [saveChangesActivated, setSaveChangesActivated] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [forceCheck, setForceCheck] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [pendingRoleAssignmentCheck, setPendingRoleAssignmentCheck] =
    useState(false)
  const [hasResolvedStaffAgencies, setHasResolvedStaffAgencies] =
    useState(false)
  const [roleConflicts, setRoleConflicts] = useState<
    UrbanXTeamByAgenciesResponse[]
  >([])
  const [errorsList, setErrorsList] = useState<string[]>([])
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const [isFormInitialised, setIsFormInitialised] = useState(false)
  const [toastState, setToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })
  const [emailInviteState, setEmailInviteState] = useState<NotificationState>(
    selectedStaffMember.emailInvitationSent
      ? NotificationState.Sent
      : NotificationState.NotSent
  )

  const [agencyIds, setAgencyIds] = useState<string[]>([])

  useEffect(() => {
    setIsFormInitialised(false)
    setStaffMemberForm({
      ...selectedStaffMember,
      roles: selectedStaffMember.roles,
    })
    setImg(selectedStaffMember.photo?.publicPath)
    setEmailInviteState(
      selectedStaffMember.emailInvitationSent
        ? NotificationState.Sent
        : NotificationState.NotSent
    )
    setIsFormDirty(false)
    setSaveChangesActivated(false)
    setErrorsList([])
    setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
    setIsFormInitialised(true)
  }, [selectedStaffMember.id, userId])

  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles()

  const availableStaffRoleOptions = useMemo(() => {
    return getAvailableStaffRoleOptions(
      staffRoleOptions,
      doesUserHaveRequiredRoles
    )
  }, [doesUserHaveRequiredRoles])

  const selectedRoles = useMemo(
    () => staffMemberForm.roles ?? [],
    [staffMemberForm.roles]
  )

  const roleIsAssignableToAgency = useMemo(() => {
    return selectedRoles.some((role) => rolesAssignableToAgency.includes(role))
  }, [selectedRoles])

  const showStateSelect = useMemo(() => {
    return selectedRoles.some((role) => rolesWithStateAccess.includes(role))
  }, [selectedRoles])

  const warningAssignableRoles = useMemo(
    () =>
      selectedRoles.filter((role) => rolesAssignableToAgency.includes(role)),
    [selectedRoles]
  )

  const selectedAssignableRoles = warningAssignableRoles

  const applyUpdatedStaffMember = (updatedForm: StaffMemberFormValues) => {
    if (isDirtyEqual(staffMemberForm, updatedForm)) {
      return
    }

    setStaffMemberForm(updatedForm)

    const hasFormChanges = !isDirtyEqual(updatedForm, selectedStaffMember)

    setIsFormDirty(hasFormChanges)
    if (hasFormChanges) setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)

    if (saveChangesActivated) {
      const updatedErrors = [
        ...new Set(validateStaffMemberForm(updatedForm, showStateSelect)),
      ]

      if (!isEqual(errorsList, updatedErrors)) {
        setErrorsList(updatedErrors)
      }
    }
  }

  const { data: agencies, isPending: agenciesPending } = useGetAllAgencies()

  const onGetAgenciesAssignedToStaffMemberSuccess = useCallback(
    (returnData: string[] | undefined) => {
      const nextAgencyIds = returnData ?? []
      setHasResolvedStaffAgencies(true)
      setAgencyIds((prev) =>
        isEqual(prev, nextAgencyIds) ? prev : nextAgencyIds
      )
    },
    []
  )

  const onGetAgenciesAssignedToStaffMemberError = useCallback(
    (error: Error) => {
      console.error('Error loading staff member agencies:', error)
      setHasResolvedStaffAgencies(true)
      setAgencyIds((prev) => (prev.length === 0 ? prev : []))
    },
    []
  )

  const { getAgenciesAssignedToStaffMember } =
    useGetAgenciesAssignedToStaffMember({
      onSuccess: onGetAgenciesAssignedToStaffMemberSuccess,
      onError: onGetAgenciesAssignedToStaffMemberError,
    })

  useEffect(() => {
    if (!roleIsAssignableToAgency || !selectedStaffMember.id) {
      setHasResolvedStaffAgencies(true)
      setAgencyIds((prev) => (prev.length === 0 ? prev : []))
      return
    }

    setHasResolvedStaffAgencies(false)
    void getAgenciesAssignedToStaffMember({
      staffMemberId: selectedStaffMember.id,
      roles: staffMemberForm.roles ?? [],
    })
  }, [
    getAgenciesAssignedToStaffMember,
    roleIsAssignableToAgency,
    selectedStaffMember.id,
    staffMemberForm.roles,
  ])

  const hasRoleAssignmentConflictCheck =
    roleIsAssignableToAgency &&
    agencyIds.length > 0 &&
    selectedAssignableRoles.length > 0

  const invalidateGetStaffMembersAssignedToAgencies =
    useInvalidateUseGetStaffMembersAssignedToAgencies()
  const agenciesApi = useContext(AgenciesApiContext)

  const invalidateManageUrbanXTeamQueries = useCallback(async () => {
    const agencyIdsToInvalidate = Array.from(
      new Set(
        [selectedAgency?.id, ...agencyIds].filter((id): id is string => !!id)
      )
    )

    if (agencyIdsToInvalidate.length > 0) {
      await Promise.all(
        agencyIdsToInvalidate.map((id) => invalidateUrbanXTeamForAgency(id))
      )
    }

    await queryClient.invalidateQueries({
      queryKey: ['getStaffByRolesLoader'],
    })
  }, [
    selectedAgency?.id,
    agencyIds,
    invalidateUrbanXTeamForAgency,
    queryClient,
  ])

  const { updateStaffMember, updateStaffMemberPending } = useUpdateStaffMember({
    onSuccess: async (_, updatedStaffMember) => {
      setIsFormDirty(false)
      await invalidateGetAllStaff()
      if (updatedStaffMember.id) {
        await invalidateGetStaff(updatedStaffMember.id)
      }
      await invalidateManageUrbanXTeamQueries()
      setSaveButtonState(SAVE_BUTTON_STATES.SAVED)
      setToastState({
        color: 'success',
        message: 'Changes saved successfully',
        open: true,
      })
    },
    onError: () => {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      setToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      })
    },
  })

  const { deleteStaffMember } = useDeleteStaffMember({
    onSuccess: async () => {
      await invalidateGetAllStaff()
      await invalidateManageUrbanXTeamQueries()
      onBack()
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      })
    },
  })

  const { assignStaffMemberToAgencies, assignStaffMemberToAgenciesPending } =
    useAssignStaffMemberToAgencies({
      onSuccess: async () => {
        setIsFormDirty(false)

        if (selectedAssignableRoles.length > 0) {
          await Promise.all(
            selectedAssignableRoles.map((role) =>
              invalidateGetStaffMembersAssignedToAgencies(role, agencyIds)
            )
          )
        }

        if (selectedStaffMember.id) {
          await getAgenciesAssignedToStaffMember({
            staffMemberId: selectedStaffMember.id,
            roles: staffMemberForm.roles ?? [],
          })
        }

        await invalidateManageUrbanXTeamQueries()

        setSaveButtonState(SAVE_BUTTON_STATES.SAVED)
      },
      onError: () => {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        setToastState({
          color: 'error',
          message: 'Error occurred assigning staff to agencies',
          open: true,
        })
      },
    })

  const confirmStaffMemberUpdate = async () => {
    setShowWarningModal(false)

    const roles = staffMemberForm.roles ?? []

    if (!selectedStaffMember.id) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      return
    }

    if (roles.length === 0) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      return
    }

    const staffRoles: StaffRole[] = roles
      .map((role) => getEnumValueByValue(StaffRole, role))
      .filter((role): role is StaffRole => role !== undefined)

    if (staffRoles.length === 0) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      return
    }

    try {
      await assignStaffMemberToAgencies({
        staffMemberId: selectedStaffMember.id,
        staffRoles,
        agencyIds: roleIsAssignableToAgency ? agencyIds : [],
      })
      await updateStaffMember(staffMemberForm)
    } catch {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      setToastState({
        color: 'error',
        message: 'Failed to update staff member',
        open: true,
      })
    }
  }

  const checkCurrentRoleAssignments = async () => {
    if (roleIsAssignableToAgency && !hasResolvedStaffAgencies) {
      return
    }

    if (hasRoleAssignmentConflictCheck) {
      try {
        const conflictResponses = await Promise.all(
          selectedAssignableRoles.map(async (role) => {
            const response =
              await agenciesApi.getStaffMembersAssignedToAgencies(
                role,
                agencyIds
              )

            return {
              staffRole: response.staffRole ?? role,
              staffRoleAssignments: response.staffRoleAssignments ?? [],
            } as UrbanXTeamByAgenciesResponse
          })
        )

        const dedupedRoleConflicts = dedupeRoleConflicts(
          conflictResponses,
          selectedStaffMember.id
        )

        if (
          warningAssignableRoles.length > 0 &&
          dedupedRoleConflicts.length > 0
        ) {
          setRoleConflicts(dedupedRoleConflicts)
          setPendingRoleAssignmentCheck(false)
          setShowWarningModal(true)
          return
        }
      } catch {
        setPendingRoleAssignmentCheck(false)
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        setToastState({
          color: 'error',
          message: 'Error validating role assignments',
          open: true,
        })
        return
      }
    }

    setRoleConflicts([])
    setPendingRoleAssignmentCheck(false)
    void confirmStaffMemberUpdate()
  }

  const onSaveChanges = () => {
    setSaveChangesActivated(true)
    const errors = [
      ...new Set(validateStaffMemberForm(staffMemberForm, showStateSelect)),
    ]
    setErrorsList(errors)

    if (errors.length > 0) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      return
    }

    setSaveButtonState(SAVE_BUTTON_STATES.SAVING)
    setPendingRoleAssignmentCheck(true)
  }

  useEffect(() => {
    if (!pendingRoleAssignmentCheck) {
      return
    }

    void checkCurrentRoleAssignments()
  }, [
    pendingRoleAssignmentCheck,
    roleIsAssignableToAgency,
    hasResolvedStaffAgencies,
    hasRoleAssignmentConflictCheck,
    warningAssignableRoles,
    selectedAssignableRoles,
    agencyIds,
    agenciesApi,
    selectedStaffMember.id,
  ])

  const { sendInvitationEmail } = useSendInvitationEmail({
    onSuccess: async () => {
      setEmailInviteState(NotificationState.Sent)
      await invalidateGetAllStaff()
      setToastState({
        color: 'success',
        message: 'Email Sent!',
        open: true,
      })
    },
    onError: () => {
      setEmailInviteState(NotificationState.NotSent)
      setToastState({
        color: 'error',
        message: 'Error sending email invitation',
        open: true,
      })
    },
  })

  const onSendInvite = () => {
    if (emailInviteState !== NotificationState.Sending) {
      setEmailInviteState(NotificationState.Sending)
      sendInvitationEmail({ staffMemberId: selectedStaffMember.id })
    }
  }

  const { mutate: uploadPublicDocument, isPending: isUploading } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (value) => {
      setImg(value?.publicPath || '')
      applyUpdatedStaffMember({
        ...staffMemberForm,
        photo: value as UploadedDocument,
      })
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error uploading photo',
        open: true,
      })
    },
  })

  const onUploadPhoto = (file: File) => {
    setImg(URL.createObjectURL(file))
    return new Promise((resolve, reject) => {
      uploadPublicDocument(
        {
          selectedFile: file,
          AgencyId: 'UrbanX',
          getAuthToken,
        },
        {
          onSuccess: resolve,
          onError: reject,
        }
      )
    })
  }

  const {
    removePublicDocument: removePhoto,
    removePublicDocumentPending: isRemovingPhoto,
  } = useRemovePublicDocument({
    onSuccess: () => {
      setImg(undefined)
      applyUpdatedStaffMember({
        ...staffMemberForm,
        photo: undefined,
      })
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error removing photo',
        open: true,
      })
    },
  })

  const isSavingChanges =
    updateStaffMemberPending || assignStaffMemberToAgenciesPending
  const isPhotoActionPending = isUploading || isRemovingPhoto

  const saveChangesState = useMemo(() => {
    if (isSavingChanges) return SAVE_BUTTON_STATES.SAVING
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR)
      return SAVE_BUTTON_STATES.ERROR
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED
    return saveButtonState
  }, [isFormDirty, isSavingChanges, saveButtonState])

  const agenciesOptions = useMemo(() => {
    return mapAgenciesToOptions(agencies, agenciesPending)
  }, [agencies, agenciesPending])

  const onBackClick = () => {
    if (isFormDirty) {
      setForceCheck(true)
      return
    }

    onBack()
  }

  if (isMissingStaffMember) return null

  const isLoading =
    allStaffMembersPending || agenciesPending || !isFormInitialised

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.TEAM}
      isStaff
    >
      <ContentLayout
        hasSecondaryMenu
        secondaryActiveMenu={MenuTitles.TEAM}
        isStaff
      >
        {isLoading ? (
          <AgxEmptyState variant="minimal" label="Loading" />
        ) : (
          <AgxSpacer direction="vertical" gap="spacing-5xl" fill>
            <FormPrompt
              hasUnsavedChanges={isFormDirty}
              forceCheck={forceCheck}
              resetForceCheck={(value: boolean) => setForceCheck(value)}
              onBack={onBack}
            />

            <AgxToast selector="#agxToast" toastState={toastState} />

            <div className="borderBottomContainer">
              <AgxButton
                id="return-button"
                variant="naked"
                text="Edit Staff Member"
                leftIcon={
                  <Icon_Arrow direction="left" height={22} width={22} />
                }
                onClick={onBackClick}
              />

              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="end"
                contentAlignment="center"
                gap="spacing-5xl"
              >
                {loggedInStaffId !== selectedStaffMember.id && (
                  <AgxButton
                    id="btn-delete-user"
                    text="Delete User"
                    variant="naked"
                    size="medium"
                    danger
                    onClick={() => setShowDeleteConfirmation(true)}
                  />
                )}

                <AgxSaveChangesButton
                  id="btnSaveChanges"
                  text="Save Changes"
                  variant="primary"
                  size="medium"
                  state={saveChangesState}
                  onClick={() => {
                    void onSaveChanges()
                  }}
                  disabled={
                    isPhotoActionPending ||
                    isSavingChanges ||
                    errorsList.length > 0 ||
                    !isFormDirty
                  }
                />
              </AgxSpacer>
            </div>

            <StaffMemberFormPage
              pageAction={StaffAction.EDIT}
              staffMember={staffMemberForm}
              photoUri={img}
              isSaving={isSavingChanges}
              isPhotoActionPending={isPhotoActionPending}
              errorsList={errorsList}
              emailErrorText={
                !staffMemberForm.email
                  ? 'Email required'
                  : !isEmailValid(staffMemberForm.email)
                    ? 'Please specify a valid email address'
                    : ''
              }
              saveChangesActivated={saveChangesActivated}
              availableStaffRoleOptions={availableStaffRoleOptions}
              stateOptions={staffStateOptions}
              showAgencies={roleIsAssignableToAgency}
              agencyIds={agencyIds}
              agenciesOptions={agenciesOptions}
              showEmailInvitation
              emailInviteState={emailInviteState}
              emailInviteDisabled={
                emailInviteState === NotificationState.Sending ||
                !isEmailValid(staffMemberForm.email) ||
                isSavingChanges
              }
              onUpdateStaffMember={applyUpdatedStaffMember}
              onAgencyIdsChange={(value: string[]) => {
                if (!isEqual(agencyIds, value)) {
                  setAgencyIds(value)
                  setIsFormDirty(true)
                  setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
                }
              }}
              onUploadPhoto={onUploadPhoto}
              onRemovePhoto={() => {
                removePhoto({
                  filePath: staffMemberForm.photo?.containerFilePath || '',
                })
              }}
              onSendInvite={onSendInvite}
            />

            <DeleteConfirmation
              name={`${selectedStaffMember.firstName} ${selectedStaffMember.lastName}`}
              title="Really delete user?"
              lineOne="Are you sure you want to delete user"
              lineTwo="This cannot be undone."
              primaryActionTitle="Permanently delete user"
              secondaryActionTitle="No, cancel"
              showPopUp={showDeleteConfirmation}
              onClose={() => setShowDeleteConfirmation(false)}
              onConfirm={() => deleteStaffMember(staffMemberForm)}
            />

            {showWarningModal && (
              <RoleAssignmentModal
                setShowModal={setShowWarningModal}
                updateRoleAssignments={confirmStaffMemberUpdate}
                selectedRoles={selectedAssignableRoles}
                roleConflicts={roleConflicts}
              />
            )}
          </AgxSpacer>
        )}
      </ContentLayout>
    </PageLayout>
  )
}

export default EditStaffMember
